// Preferências do usuário (PO1-2157, sucessor do placeholder da PO1-1918).
// Layout alinhado com a tela de Meu Perfil: container hero + SubCards,
// mesmas cores/tokens. Hoje contém só o produto preferido; pode ganhar
// mais campos futuramente (perfis de atuação do Autopilot, etc).

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import api from "../../../services/api";
import { useAuth } from "../../../hooks/auth";
import { rotaInicial } from "../../../routes/products";
import { useFeatures } from "../../../hooks/features";
import { useToast } from "../../../hooks/toast";
import { PRODUCT_META as PRODUCTS_CATALOG } from "../../../routes/products";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

// Reaproveita o catálogo do AppSwitcher pra alinhar visual (mesmo ícone, mesma cor).
const PRODUCT_META = PRODUCTS_CATALOG;

const STORAGE_KEY = "produtoAtual";

// ─── helpers visuais alinhados com tela Perfil ─────────────────────────────

const closeButtonStyle = {
  position: "absolute",
  top: 14,
  right: 18,
  zIndex: 1,
  border: 0,
  background: "transparent",
  color: "#d7e7ff",
  fontSize: "2rem",
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
  opacity: 0.95,
  textShadow: "none",
};

const editableInputStyle = {
  color: AUTOPILOT_COLORS.title,
  fontWeight: 600,
  borderColor: AUTOPILOT_COLORS.border,
};

function SubCard({ title, children, style }) {
  return (
    <div
      style={{
        border: `1px solid ${AUTOPILOT_COLORS.border}`,
        borderRadius: 14,
        padding: "14px 18px",
        marginBottom: 14,
        background: AUTOPILOT_COLORS.insetSurface,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {title && (
        <>
          <p
            style={{
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: AUTOPILOT_COLORS.accent,
              margin: "0 0 0.5rem 0",
            }}
          >
            {title}
          </p>
          <div
            style={{
              borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
              marginBottom: "0.85rem",
            }}
          />
        </>
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
}

const Preferencias = () => {
  const { addToast } = useToast();
  const { updateUser, user } = useAuth();
  const { codes, loading: featuresLoading } = useFeatures();
  const history = useHistory();
  // PO1-2287: fecha na aterrissagem do setor (transportadora não tem Autopilot).
  const handleClose = () => history.push(rotaInicial(user));

  const [loading, setLoading] = useState(true);
  const [produtoPadrao, setProdutoPadrao] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/users/me");
      setProdutoPadrao(response.data.produtoPadrao || "");
    } catch (_) {
      setError("Não foi possível carregar suas preferências.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const eligibleOptions = React.useMemo(() => {
    if (!codes) return [];
    // PO1-2287: MEUFRETE entra para a transportadora (é a única feature dela).
    return ["FLOW", "GOBID", "TRACKFY", "AUTOPILOT", "MEUFRETE"].filter((c) => codes.has(c));
  }, [codes]);

  const selectedMeta =
    produtoPadrao && PRODUCT_META[produtoPadrao]
      ? PRODUCT_META[produtoPadrao]
      : null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.put("/users/me", { produtoPadrao: produtoPadrao || null });

      // Sincroniza com o localStorage pra refletir imediatamente sem reload.
      // Se "sem preferência" (vazio), apaga a chave — fallback PRODUCT_ORDER assume.
      if (typeof window !== "undefined") {
        if (produtoPadrao) {
          window.localStorage.setItem(STORAGE_KEY, produtoPadrao);
        }
      }
      updateUser({ produtoPadrao: produtoPadrao || null });

      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: "Preferência salva",
      });
    } catch (err) {
      setError(
        (err.response && err.response.data && err.response.data.error) ||
          "Falha ao salvar preferência.",
      );
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loading || featuresLoading;

  return (
    <div className="content">
      <Container fluid>
        <Card
          style={{
            ...autopilotCardStyle,
            background: "var(--everlog-card-hero-bg)",
            position: "relative",
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            title="Fechar"
            style={closeButtonStyle}
          >
            ×
          </button>

          <CardHeader style={autopilotHeaderStyle}>
            <div style={autopilotEyebrowStyle}>Minha Conta</div>
            <div style={{ ...autopilotTitleStyle, marginBottom: "0.25rem" }}>
              Preferências
            </div>
            <p style={{ ...autopilotDescriptionStyle, marginBottom: 0 }}>
              Personalize como o Hub abre pra você. As escolhas aqui são por
              usuário e seguem em qualquer dispositivo.
            </p>
          </CardHeader>

          <CardBody style={autopilotBodyStyle}>
            {isLoading ? (
              <p style={{ color: AUTOPILOT_COLORS.muted, margin: 0 }}>
                Carregando...
              </p>
            ) : (
              <Row>
                {/* ─── Coluna identidade do produto ativo ─── */}
                <Col lg="4" md="5">
                  <SubCard>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "0.4rem 0",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 96,
                          height: 96,
                          borderRadius: 24,
                          margin: "0 auto",
                          background: selectedMeta
                            ? `color-mix(in srgb, ${selectedMeta.color} 18%, transparent)`
                            : AUTOPILOT_COLORS.surfacePill,
                          color: selectedMeta
                            ? selectedMeta.color
                            : AUTOPILOT_COLORS.muted,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selectedMeta && selectedMeta.iconImage ? (
                          <img
                            src={selectedMeta.iconImage}
                            alt=""
                            style={{
                              width: 56,
                              height: 56,
                              objectFit: "contain",
                            }}
                          />
                        ) : selectedMeta && selectedMeta.icon ? (
                          React.createElement(selectedMeta.icon, { size: 56 })
                        ) : (
                          <span
                            style={{
                              fontSize: "2.2rem",
                              fontWeight: 800,
                              letterSpacing: "0.05em",
                            }}
                          >
                            —
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          ...autopilotTitleStyle,
                          marginTop: "1rem",
                          marginBottom: "0.15rem",
                        }}
                      >
                        {selectedMeta
                          ? selectedMeta.label
                          : "Sem preferência"}
                      </div>
                      <div
                        style={{
                          color: AUTOPILOT_COLORS.muted,
                          fontSize: "0.78rem",
                        }}
                      >
                        Produto preferido
                      </div>
                      <div
                        style={{
                          display: "inline-flex",
                          marginTop: "0.85rem",
                          padding: "0.2rem 0.7rem",
                          borderRadius: 999,
                          background: AUTOPILOT_COLORS.surfacePill,
                          color: AUTOPILOT_COLORS.textSoft,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {selectedMeta
                          ? "Default deste user"
                          : "Usa o padrão do sistema"}
                      </div>
                    </div>
                  </SubCard>
                </Col>

                {/* ─── Coluna formulário ─── */}
                <Col lg="8" md="7">
                  <SubCard title="Produto preferido">
                    <p
                      style={{
                        ...autopilotDescriptionStyle,
                        marginBottom: "0.9rem",
                      }}
                    >
                      Define qual produto abre como ativo em um dispositivo
                      novo (ou quando o cache local é limpo). Trocar pelo app
                      switcher do header sobrescreve temporariamente neste
                      device.
                    </p>
                    <Form onSubmit={handleSave}>
                      <FormGroup>
                        <Label>Produto</Label>
                        <Input
                          type="select"
                          value={produtoPadrao}
                          onChange={(e) => setProdutoPadrao(e.target.value)}
                          style={editableInputStyle}
                        >
                          <option value="">
                            Sem preferência (usar padrão)
                          </option>
                          {eligibleOptions.map((code) => (
                            <option key={code} value={code}>
                              {PRODUCT_META[code]?.label || code}
                            </option>
                          ))}
                        </Input>
                        <small
                          style={{
                            color: AUTOPILOT_COLORS.muted,
                            fontSize: "0.76rem",
                          }}
                        >
                          Somente produtos contratados pelo seu embarcador
                          aparecem aqui. Se o produto preferido deixar de ser
                          contratado, o default volta automaticamente.
                        </small>
                      </FormGroup>

                      {error && (
                        <Alert color="danger" style={{ marginTop: "0.5rem" }}>
                          {error}
                        </Alert>
                      )}

                      <div
                        style={{
                          marginTop: "0.5rem",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          type="submit"
                          style={autopilotSecondaryButtonStyle}
                          disabled={saving}
                        >
                          {saving ? "Salvando..." : "Salvar preferência"}
                        </Button>
                      </div>
                    </Form>
                  </SubCard>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Preferencias;
